import categoryBirdsAnimals from '@echo/brand/assets/category-birds-animals.png';
import categoryExplorationDiscovery from '@echo/brand/assets/category-exploration-discovery.png';
import categoryFriendshipKindness from '@echo/brand/assets/category-friendship-kindness.png';
import categoryHomeBelonging from '@echo/brand/assets/category-home-belonging.png';
import categoryPlantsTrees from '@echo/brand/assets/category-plants-trees.png';
import categoryPlayImagination from '@echo/brand/assets/category-play-imagination.png';
import categoryRiversPondsSeas from '@echo/brand/assets/category-rivers-ponds-seas.png';
import categorySeasonsWeather from '@echo/brand/assets/category-seasons-weather.png';
import navLibrary from '@echo/brand/assets/nav-library.png';
import { type LibraryEntry, libraryParamsSchema, libraryShelves, searchLibrary } from '@echo/core';
import { type MathEntry, useLibrary, useMathLibrary, useSaved } from '@echo/core/react';
import { Button, SearchField, SegmentedControl } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Search } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { CategoryGrid, LibraryRow, ListCard, MathRow } from '@/components/library-items';
import { MATH_ART, mathArt } from '@/lib/math-art';

/**
 * `?shelf=math` is the Math shelf, so coming back from a Math page returns to it; `?from=` is the
 * exercise whose "Choose from Library" opened it, and keeps it to that shelf.
 */
export const Route = createFileRoute('/_app/library')({
  component: LibraryPage,
  validateSearch: (search: Record<string, unknown>) => libraryParamsSchema.parse(search),
});

// The library's category illustrations (Figma library), by category key; a category the
// library adds later shows the reading illustration until it has its own.
const CATEGORY_ART: Record<string, string> = {
  'friendship-kindness': categoryFriendshipKindness,
  'play-imagination': categoryPlayImagination,
  'home-belonging': categoryHomeBelonging,
  'plants-trees': categoryPlantsTrees,
  'birds-animals': categoryBirdsAnimals,
  'seasons-weather': categorySeasonsWeather,
  'rivers-ponds-seas': categoryRiversPondsSeas,
  'exploration-discovery': categoryExplorationDiscovery,
};

/**
 * Library (Figma library), Reading / Math with a search. Reading is the read-aloud library
 * from Core: its categories, each opening its own page of books (covers, title, author, the
 * category and reading time), and search by title or author. Math is the
 * Math library from Core: its categories, each opening its own page of rows (a variation,
 * its category and level), and search by title, type or level. A book or a row opens its
 * info, where it can be started or favorited; each shelf lists its favorites under
 * My favorites. "For you" is hidden for now, and Math where the environment has no Math library.
 */
function LibraryPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const math = useMathLibrary();
  const search = Route.useSearch();
  const { shelf, switchable } = libraryShelves(search, math.available);
  const { from } = search;
  const library = useLibrary();
  const saved = useSaved();
  const [query, setQuery] = useState('');
  // A book opens its info first (Figma library › book info): read it or save it from there.
  const openBook = (entry: LibraryEntry) =>
    void navigate({ to: '/library/book/$lineageUuid', params: { lineageUuid: entry.lineageUuid } });
  const openSet = (entry: MathEntry) =>
    void navigate({
      to: '/library/math/set/$lineageUuid',
      params: { lineageUuid: entry.lineageUuid },
    });
  const searching = query.trim().length > 0;
  // Here the tag is the book's first category (a category's own page tags it with that one).
  const row = (entry: LibraryEntry) => (
    <LibraryRow
      key={entry.lineageUuid}
      entry={entry}
      tags={library.tagsFor(entry)}
      onClick={() => openBook(entry)}
    />
  );
  // My favorites, in the order they were saved: books on the Reading shelf, rows on the Math one.
  const savedEntries = saved.savedLineages.flatMap((lineage) => {
    const entry = library.reading.find((candidate) => candidate.lineageUuid === lineage);
    return entry ? [entry] : [];
  });
  const savedMath = math.entriesFor(saved.savedLineages);

  const mathRow = (entry: MathEntry) => (
    <MathRow
      key={entry.lineageUuid}
      title={entry.title}
      byline={entry.byline}
      tags={entry.tags}
      art={{ src: mathArt(entry) }}
      onClick={() => openSet(entry)}
    />
  );

  // Loading, failed or empty: the same for both shelves.
  const notReady = (state: {
    ready: boolean;
    failed: boolean;
    empty: boolean;
    retry: () => void;
  }): ReactNode => {
    if (!state.ready) {
      return (
        <div className="flex justify-center py-section-sm">
          <span
            aria-hidden
            className="size-10 animate-spin rounded-full border-4 border-action-primary border-t-transparent"
          />
        </div>
      );
    }
    if (state.failed) {
      return (
        <div className="flex flex-col items-start gap-content-sm">
          <p className="text-body-md text-on-surface-medium">
            <Trans>The library couldn't be loaded.</Trans>
          </p>
          <Button variant="secondary" onClick={state.retry}>
            {t`Try again`}
          </Button>
        </div>
      );
    }
    if (state.empty) {
      return (
        <p className="text-body-md text-on-surface-medium">
          <Trans>There's nothing here yet.</Trans>
        </p>
      );
    }
    return null;
  };
  const mathShelf = (): ReactNode => {
    if (searching)
      return <Results query={query} rows={searchLibrary(math.entries, query).map(mathRow)} />;
    const waiting = notReady({ ...math, empty: math.entries.length === 0 });
    if (waiting) return waiting;
    return (
      <>
        <CategoryGrid
          title={t`Browse by category`}
          items={math.categories.map(({ key, label }) => ({
            key,
            label,
            art: { src: MATH_ART[key] },
            onClick: () =>
              void navigate({ to: '/library/math/$category', params: { category: key } }),
          }))}
        />
        {savedMath.length > 0 ? (
          <ListCard title={t`My favorites`} subtitle={t`Math you saved as favorites`}>
            {savedMath.map(mathRow)}
          </ListCard>
        ) : null}
      </>
    );
  };

  const readingShelf = (): ReactNode => {
    if (searching)
      return <Results query={query} rows={searchLibrary(library.reading, query).map(row)} />;
    const waiting = notReady({
      ready: library.ready,
      failed: library.failed,
      empty: library.reading.length === 0,
      retry: library.retry,
    });
    if (waiting) return waiting;
    return (
      <>
        <CategoryGrid
          title={t`Browse by category`}
          items={library.categories.map(({ key, label }) => ({
            key,
            label,
            art: { src: CATEGORY_ART[key] ?? navLibrary },
            onClick: () =>
              void navigate({ to: '/library/reading/$category', params: { category: key } }),
          }))}
        />
        {savedEntries.length > 0 ? (
          <ListCard title={t`My favorites`} subtitle={t`Books you saved as favorites`}>
            {savedEntries.map(row)}
          </ListCard>
        ) : null}
      </>
    );
  };

  return (
    <>
      {from ? (
        <div className="flex min-h-tap-target items-center">
          <Button
            variant="ghost"
            onClick={() =>
              canGoBack
                ? router.history.back()
                : navigate({ to: '/exercise/$kind/choose', params: { kind: from } })
            }
          >
            <ArrowLeft size={24} aria-hidden />
            {t`Back`}
          </Button>
        </div>
      ) : null}
      <h1 className="text-display-sm font-bold">
        <Trans>Library</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm pt-content-md">
        <div className="flex flex-col gap-content-sm">
          {switchable ? (
            <SegmentedControl
              label={t`Library`}
              options={[
                { value: 'reading', label: t`Reading` },
                { value: 'math', label: t`Math` },
              ]}
              value={shelf}
              onChange={(next) => {
                setQuery('');
                void navigate({ to: '/library', search: next === 'math' ? { shelf: next } : {} });
              }}
            />
          ) : null}
          <SearchField
            label={
              shelf === 'math' ? t`Search by title, type or level` : t`Search by title or author`
            }
            icon={<Search size={24} className="shrink-0 text-icon-medium" aria-hidden />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </div>

        {shelf === 'math' ? mathShelf() : readingShelf()}
      </div>
    </>
  );
}

/** What a search found, or that it found nothing. */
function Results({ query, rows }: { query: string; rows: ReactNode[] }) {
  const { t } = useLingui();
  return (
    <ListCard title={t`Results`}>
      {rows.length === 0 ? (
        <p className="text-body-md text-on-surface-medium" aria-live="polite">
          <Trans>Nothing matches “{query}”.</Trans>
        </p>
      ) : (
        rows
      )}
    </ListCard>
  );
}
